import React, { useState, useMemo, useEffect, useRef, startTransition } from "react";
import {
  Collapse,
  Input,
  Table,
  Empty,
  Breadcrumb,
  Spin,
  Modal,
  Dropdown,
  Menu,
  message,
  Tooltip,
  Button,
  Switch,
} from "antd";
import {
  DownloadOutlined,
  DeleteOutlined,
  FolderAddOutlined,
  UploadOutlined,
  SettingOutlined,
  MoreOutlined,
  EditOutlined,
  CopyOutlined,
  UserAddOutlined,
  SyncOutlined,
  EyeOutlined,
  RightOutlined,
  DownOutlined,
} from "@ant-design/icons";
import { Box } from "@mui/material";
import { useMsal } from "@azure/msal-react";
import JSZip from "jszip";
import {
  checkIsRegistered,
  getS3DownloadUrl,
  registerAssetInDatazone,
  createS3Folder,
  getAllFilesInFolder,
  getAgCreds,
  IGetAgCredsResponse,
  getObjectVersions,
  getVersionsSummaryForPrefix,
} from "./objectListApi";
import UploadComponent, { UploadHandle } from "components/Upload";
import { deleteFilesAPI } from "components/Delete/deleteApi";
import { deleteCustomAttachedFilesystem } from "./fsxApi";
import DeleteFileModal from "components/Delete";
import AddOrEditSpace from "components/AddOrEditSpace";
import "./style.scss";
import { DISABLE_EMPTYSPACE } from "const";
import Spinner from "common/Spinner";
import FormSkeleton, { ISkeletonField } from "common/skeleton";
import { getLoggedInUser } from "utils/utils";
import FilePreviewModal from "components/FilePreviewModal";
import { ENABLE_FILE_PREVIEW, NEXUS_GXP_UPLOAD_DISABLE } from "const";
import { CombinedVersion, getObjectVersionsPage } from "components/InlineVersionsTable/inlineVersionsApi";
import InlineVersionsTable from "components/InlineVersionsTable";

// File item interface
interface FileItem {
  key: string;
  name: string;
  lastModified: string | null;
  timestamp: string;
  class: string;
  size: number;
  isFolder: boolean;
  region: string;
  bucket: string;
  relativePath?: string;
  hasPreviousVersions?: boolean;
  versionCount?: number;
  previousVersionCount?: number;
}

export interface IGetAgCreds {
  bucket_name: string,
  region: string,
  data_account_id: string,
  space_name: string,
  access_level?: "READ" | "READWRITE";
  at?: string,
}

export const formatBytes = (bytesStr: string): string => {
  const bytes = parseInt(bytesStr, 10);
  if (isNaN(bytes)) return bytesStr;
  if (bytes === 0) return "-";
  const k = 1024,
    sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

// Util for time ago
export function timeAgo(dateString: string | null): string {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "-";
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? "s" : ""} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours !== 1 ? "s" : ""} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days !== 1 ? "s" : ""} ago`;
}

export function parseS3XmlToItems(data: any, region: any, spaceName = ""): FileItem[] {
  const items: FileItem[] = [];
  const prefix = data?.Prefix || "";

  if (Array.isArray(data.CommonPrefixes)) {
    data.CommonPrefixes.forEach((p: { Prefix: string; __isDeleted?: boolean }) => {
      const key = p.Prefix;
      const name = key.split("/").filter(Boolean).pop() || key;
      const isDeleted = p.__isDeleted || false;

      items.push({
        key,
        name: isDeleted ? `${name} (deleted)` : name,
        lastModified: null,
        timestamp: "",
        class: isDeleted ? "DELETED" : "Folder",
        size: 0,
        isFolder: true,
        bucket: spaceName,
        region,
        __isDeleted: isDeleted,
      } as FileItem & { __isDeleted?: boolean });
    });
  }

  if (Array.isArray(data.Contents)) {
    data.Contents.forEach(
      (c: { Key: string; LastModified: string; Size: number; StorageClass?: string; __isDeleted?: boolean }) => {
        const key = c.Key;
        if (key === prefix) return;
        const name = key.split("/").filter(Boolean).pop() || key;

        // Handle deleted objects
        const isDeleted = c.__isDeleted || c.StorageClass === "DELETED";

        items.push({
          key,
          name: isDeleted ? `${name} (deleted)` : name,
          lastModified: c.LastModified,
          timestamp: new Date(c.LastModified).toISOString(),
          class: isDeleted ? "DELETED" : (c.StorageClass || "Standard"),
          size: c.Size,
          isFolder: false,
          bucket: spaceName,
          region,
          __isDeleted: isDeleted,
        } as FileItem & { __isDeleted?: boolean });
      },
    );
  }
  return items;
}

// const fetchAndDownloadZip = async (
//   fileBlobs: { file: FileItem; blob: Blob }[],
//   zipName = "files.zip",
// ) => {
//   const zip = new JSZip();
//   fileBlobs.forEach(({ file, blob }) => {
//     const fileName = file.name || file.key.split("/").pop() || "file";
//     zip.file(fileName, blob);
//   });
//   const blob = await zip.generateAsync({ type: "blob" });
//   const link = document.createElement("a");
//   link.href = URL.createObjectURL(blob);
//   link.download = zipName;
//   document.body.appendChild(link);
//   link.click();
//   link.remove();
// };

export const fetchAndDownloadZip = async (
  fileBlobs: { file: FileItem; blob: Blob }[],
  zipName = "files.zip",
) => {
  const zip = new JSZip();
  fileBlobs.forEach(({ file, blob }) => {
    // if file.relativePath is set, use it in zip, otherwise use file.name
    const filePath = file.relativePath || file.key || file.name;
    zip.file(filePath, blob);
  });
  const blob = await zip.generateAsync({ type: "blob" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = zipName;
  document.body.appendChild(link);
  link.click();
  link.remove();
};


const formSkeleton: ISkeletonField[][] = [
  // Row 1
  [
    { type: "input", span: 24 },
  ],
  // Row 2
  [
    { type: "input", span: 24 },
  ],
  [
    { type: "input", span: 24 },
  ],
  [
    { type: "input", span: 24 },
  ],
];

interface FileGridProps {
  domainName: string;
  spaceName: string;
  spaceDisplayName: string | "";
  selectedRegion: string;
  currentPrefix: any;
  selecteSpaceRegion: any;
  selectedBucket: string;
  onFolderClick?: (spaceName: string, selecteSpaceRegion: string, prefix: string, bucketName: string, spaceDisplayName: string) => Promise<any> | void;
  onRefreshSpaces?: () => void;
  gxpFlag?: string;
  DataAccountId: string;
  fileSystemType?: string;
  setSelectedSpace?: (space: any) => void;
}


const FileGrid = ({
  domainName,
  spaceName,
  spaceDisplayName,
  selectedRegion,
  currentPrefix,
  selecteSpaceRegion,
  selectedBucket,
  onFolderClick,
  onRefreshSpaces,
  gxpFlag,
  DataAccountId,
  fileSystemType,
  setSelectedSpace,
}: FileGridProps) => {
  // Ref for scrolling to ObjectList view
  const objectListRef = useRef<HTMLDivElement>(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [emptySpaceKeys, setEmptySpaceKeys] = useState<string[]>([]);
  const [isEmptySpaceMode, setIsEmptySpaceMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [prefixTrail, setPrefixTrail] = useState<string[]>([]);
  const [registeredKeys, setRegisteredKeys] = useState<string[]>([]);
  const [agCredsResponseData, setAgCredsResponseData] = useState<IGetAgCredsResponse | boolean>(false);
  // Scroll to ObjectList view when agCredsResponseData is valid
  useEffect(() => {
    if (agCredsResponseData && typeof agCredsResponseData === "object") {
      objectListRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [agCredsResponseData]);
  const [objectList, setObjectList] = useState<{
    data: any;
    isTruncated?: boolean;
    nextContinuationToken?: string;
  } | null>(null);

  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const msalInstance = useMsal();
  const uploadRef = useRef<UploadHandle>(null);
  const autoClickedLandingRef = useRef(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewFileUrl, setPreviewFileUrl] = useState<string | null>(null);
  const [previewFileName, setPreviewFileName] = useState<string>("");
  // Auto-drill for --landing
  const autoLandingRef = useRef(false);
  // Add Folder Modal state
  const [isAddFolderModalOpen, setIsAddFolderModalOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [addFolderError, setAddFolderError] = useState("");
  // NEW
  const [showVersions, setShowVersions] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  const [rowVersionMountKeyByKey, setRowVersionMountKeyByKey] =
    useState<Record<string, number>>({});

  const [versionSummary, setVersionSummary] =
    useState<Record<string, { count: number; hasPrevious: boolean }>>({});

  const [pageTokens, setPageTokens] = useState<(string | undefined)[]>([undefined]);
  const [lastS3NextToken, setLastS3NextToken] = useState<string | undefined>(undefined);

  // Check if upload should be disabled for GxP spaces
  const gxpUploadDisabled = NEXUS_GXP_UPLOAD_DISABLE;
  const isGxpSpace = gxpFlag?.toLowerCase() === "gxp";
  const isUploadDisabled = isGxpSpace && gxpUploadDisabled;

  // Check if current user has write permissions
  const currentUserHasWriteAccess = useMemo(() => {
    const loggedInUser = getLoggedInUser();
    if (!loggedInUser) return false;

    if (agCredsResponseData && typeof agCredsResponseData === "object" && agCredsResponseData.user_role) {
      return agCredsResponseData.user_role === "owner" || agCredsResponseData.user_role === "rw";
    }

    return false;
  }, [agCredsResponseData]);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [versionsTargetName, setVersionsTargetName] = useState<string>("");
  const [versionsData, setVersionsData] = useState<any[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsTarget, setVersionsTarget] = useState<FileItem | null>(null);
  const [versionsToggleKey, setVersionsToggleKey] = useState<string | null>(null);

 const openVersionsFromRow = (rec: FileItem) => {
    setVersionsTarget(rec);
    setVersionsOpen(true);
    setVersionsToggleKey(rec.key);
  };

  // Helper function to get current user's role
  const currentUserRole: string | null = useMemo(() => {
    const loggedInUser = getLoggedInUser();
    if (!loggedInUser) return null;

    // Check agCredsResponseData which includes both individual and group-based permissions
    if (agCredsResponseData && typeof agCredsResponseData === "object" && agCredsResponseData.user_role) {
      return agCredsResponseData.user_role;
    }

    return null;
  }, [agCredsResponseData]);



  // Fetch AG creds only when spaceName, selecteSpaceRegion, DataAccountId, or spaceDisplayName changes
  useEffect(() => {
    let cancelled = false;
    const fetchCreds = async () => {
      setLoading(true);
      setApiError(null);
      try {
        const { getAgCreds } = await import("./objectListApi");
        const agCreds = await getAgCreds(msalInstance, {
          bucket_name: spaceName,
          region: selecteSpaceRegion,
          data_account_id: DataAccountId,
          space_name: spaceDisplayName,
          access_level: "READ",
        });
        if (!cancelled) setAgCredsResponseData(agCreds);
        if (agCreds === false) {
          message.error("Failed to get access credentials. Please try again later.");
          if (typeof setSelectedSpace === "function") {
            setSelectedSpace(null);
          }
        }
      } catch (err: any) {
        if (!cancelled) {
          console.log("Caught error in component:", err);

          // Check for 403 permission error - handle both axios and enhanced error formats
          const status = err.status || err.response?.status;
          const errorDetail = err.detail || err.response?.data?.detail;

          if (status === 403) {
            // For 403 errors, use a more specific message
            const errorMessage = errorDetail || "You don't have permission to view this Space";
            console.log("Showing 403 error message:", errorMessage);
            message.error(errorMessage);
            setApiError(errorMessage);
          } else {
            // For other errors, show the detail if available, otherwise generic message
            const errorMessage = errorDetail || err.message || "Failed to get access credentials.";
            console.log("Showing generic error message:", errorMessage);
            setApiError(errorMessage);
            message.error(errorMessage.includes("credentials") ? errorMessage : "Failed to get access credentials. Please try again later.");
          }

          // Clear the selected space if there's an error
          if (typeof setSelectedSpace === "function") {
            setSelectedSpace(null);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (spaceName && selecteSpaceRegion && DataAccountId && spaceDisplayName) {
      fetchCreds();
    }
    return () => { cancelled = true; };
  }, [spaceName, selecteSpaceRegion, DataAccountId, spaceDisplayName, msalInstance]);

  useEffect(() => {
    let cancelled = false;
    const fetchObjects = async () => {
      if (!spaceName || !selecteSpaceRegion || !agCredsResponseData) return;

      setLoading(true);
      setApiError(null);
      try {
        // 👇 get the token for THIS page (AntD pages are 1-based)
        const tokenForThisPage = pageTokens[currentPage - 1];

        const basePrefix = prefixTrail.join("");
        const effectivePrefix = debouncedSearch
          ? `${basePrefix}${debouncedSearch}`
          : basePrefix;

        const s3MaxKeys = pageSize;

        const objectListResp = showVersions
          ? await (await import("./objectListApi")).getObjectsWithVersions(
            msalInstance,
            spaceName,
            selecteSpaceRegion,
            effectivePrefix,
            agCredsResponseData,
            {
              maxKeys: s3MaxKeys,
              continuationToken: tokenForThisPage,
            },
          )
          : await (await import("./objectListApi")).getObjectsByDomainName(
            msalInstance,
            [spaceName],
            selecteSpaceRegion,
            effectivePrefix,
            agCredsResponseData,
            {
              maxKeys: pageSize,
              continuationToken: tokenForThisPage,
            },
          );

        if (!cancelled) {
          setObjectList(objectListResp ?? { data: [] });
          // 👇 save the "next" token S3 gave us for THIS page
          setLastS3NextToken(objectListResp?.nextContinuationToken);

          const versionSummaryResp = await getVersionsSummaryForPrefix(
            msalInstance,
            spaceName,
            selecteSpaceRegion,
            effectivePrefix,
            agCredsResponseData,
          );
          setVersionSummary(versionSummaryResp);


        }
      } catch (err: any) {
        if (!cancelled) {
          setApiError("Failed to load objects. Please try again.");
          setObjectList({ data: [] });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchObjects();
    return () => { cancelled = true; };
  }, [
    spaceName,
    selecteSpaceRegion,
    prefixTrail,
    agCredsResponseData,
    msalInstance,
    refreshKey,
    debouncedSearch,
    showVersions,
    pageSize,
    currentPage,
    pageTokens,
  ]);


  useEffect(() => {
    // wait for user to stop typing
    const id = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());

      // whenever search changes → go back to page 1
      setCurrentPage(1);
      setPageTokens([undefined]);
      setLastS3NextToken(undefined);
    }, 500); // 500ms debounce

    return () => clearTimeout(id);
  }, [searchTerm]);


  // // DataSource from API
  const dataSource: FileItem[] = useMemo(() => {
    if (!objectList || typeof objectList.data !== "object" || Array.isArray(objectList.data)) return [];

    const base = parseS3XmlToItems(objectList.data, selecteSpaceRegion, spaceName);

    // Merge version info for files
    return base.map(item => {
      if (item.isFolder) return item;
      const entry = versionSummary[item.key];
      const previousCount = entry?.count ?? 0; // This is already the previous versions count
      return {
        ...item,
        hasPreviousVersions: previousCount > 0,
        versionCount: previousCount + 1, // Total = previous + current
        previousVersionCount: previousCount, // Previous versions only
      };
    });
  }, [objectList, spaceName, selectedRegion, selecteSpaceRegion, versionSummary]);



  // console.log("==dataSource==", selecteSpaceRegion);
  // Filtered for search
  const filteredData = useMemo(() =>
    dataSource.filter((i) =>
      i.name.toLowerCase().includes(searchTerm.toLowerCase()),
    ),
    [dataSource, searchTerm],
  );


  // Folder navigation
  const handleFolderClick = async (folderName: string) => {
    const normalizedFolder = folderName.replace(/\/+$/, "") + "/";
    const nextTrail = [...prefixTrail, normalizedFolder];
    if (nextTrail.join("") === prefixTrail.join("")) {
      return;
    }

    try {

      await onFolderClick!(spaceName, selecteSpaceRegion, nextTrail.join(""), selectedBucket, spaceDisplayName);
      setPrefixTrail(nextTrail);
      setSearchTerm("");
      setCurrentPage(1);
      setPageTokens([undefined]);
      setLastS3NextToken(undefined);
      autoLandingRef.current = false;
    } catch (err: any) {
      if (err.$metadata?.httpStatusCode === 403) {
        message.open({
          type: "error",
          content: "Access Denied",
          className: "access-denied-message",
        });
      } else {
        message.open({
          type: "error",
          content: "Failed to open folder",
          className: "access-denied-message",
        });
      }
    }
  };


  useEffect(() => {
    if (loading) return;
    if (prefixTrail.length !== 1) return; // Only in first subfolder, not root or deeper
    const landing = filteredData.find(
      (item) => item.isFolder && item.name === "--landing",
    );
    // if (landing) {
    //   handleFolderClick("--landing/");
    // }

  }, [filteredData, loading, prefixTrail]);


  useEffect(() => {
    // Update prefixTrail if currentPrefix from parent changes
    if (currentPrefix !== prefixTrail.join("")) {
      const trail = currentPrefix
        ? currentPrefix.split("/").filter(Boolean).map((p: string) => p + "/")
        : [];
      setPrefixTrail(trail);
    }
  }, [currentPrefix]);



  // For region, bucket extraction (for deletion, etc)
  const region = useMemo(() => {
    return filteredData[0]?.region || dataSource[0]?.region || "us-east-1";
  }, [filteredData, dataSource]);



  const bucket = spaceName;

  // Registration status effect
  // useEffect(() => {
  //   let mounted = true;
  //   (async () => {
  //     const visible = filteredData.filter((f) => !f.isFolder);
  //     const newly: string[] = [];
  //     for (const f of visible) {
  //       if (!registeredKeys.includes(f.key)) {
  //         if (await checkIsRegistered(f.bucket, f.key, msalInstance, selecteSpaceRegion)) {
  //           newly.push(f.key);
  //         }
  //       }
  //     }
  //     if (mounted && newly.length) {
  //       setRegisteredKeys((p) => Array.from(new Set([...p, ...newly])));
  //     }
  //   })();
  //   return () => {
  //     mounted = false;
  //   };
  // }, [filteredData, msalInstance, registeredKeys]);

  const handleBreadcrumbClick = (idx: number) => {
    const trail = prefixTrail.slice(0, idx + 1);
    setPrefixTrail(trail);
    onFolderClick?.(spaceDisplayName, selecteSpaceRegion, trail.join(""), selectedBucket, spaceDisplayName);
    setSearchTerm("");

    setCurrentPage(1);
    setPageTokens([undefined]);
    setLastS3NextToken(undefined);
  };

  const getCurrentFolderName = () =>
    prefixTrail.length === 0
      ? "files"
      : prefixTrail[prefixTrail.length - 1].replace(/\/$/, "");


  const downloadSelected = async () => {
    if (!selectedFiles.length)
      return message.warning("No files or folders selected.");

    const sel = filteredData.filter((i) =>
      selectedFiles.includes(i.key),
    );

    // Filter out deleted objects
    const validSel = sel.filter((i) => !(i as any).__isDeleted);

    if (validSel.length === 0) {
      return message.error("No downloadable files selected. Deleted objects cannot be downloaded.");
    }

    // Fetch fresh credentials before downloading
    const credEnd = message.loading("Fetching credentials...", 0);
    let freshAgCreds: IGetAgCredsResponse | boolean;
    try {
      freshAgCreds = await getAgCreds(msalInstance, {
        bucket_name: spaceName,
        region: selecteSpaceRegion,
        data_account_id: DataAccountId,
        space_name: spaceDisplayName,
        access_level: "READ",
      });
      
      if (freshAgCreds === false) {
        credEnd();
        message.error("Failed to get access credentials. Please try again later.");
        return;
      }
    } catch (err: any) {
      credEnd();
      console.error("Error fetching credentials:", err);
      message.error("Failed to get access credentials. Please try again later.");
      return;
    }
    credEnd();

    // Separate out files and folders (only valid ones)
    const files = validSel.filter((i) => !i.isFolder);
    const folders = validSel.filter((i) => i.isFolder);

    // Recursively get all files inside selected folders
    let filesFromFolders: any = [];
    for (const folder of folders) {
      const folderFiles = await getAllFilesInFolder(
        folder.key,
        folder.bucket,
        msalInstance,
        selecteSpaceRegion,
        freshAgCreds,
      );
      // Add a relativePath property for zip structure
      folderFiles.forEach(f => {
        let relPath = String(f.key).substring(folder.key.length);
        if (relPath.startsWith("/")) relPath = relPath.substring(1);
        f.relativePath = String(folder.name) + (relPath ? "/" + relPath : "");
      });

      filesFromFolders = filesFromFolders.concat(folderFiles);
    }

    // Add a relativePath for top-level selected files (to appear in zip root)
    files.forEach(f => { f.relativePath = f.name; });

    // Combine all files
    const allFiles = [...files, ...filesFromFolders];

    if (!allFiles.length) {
      return message.error("No downloadable files found in selection.");
    }

    const end = message.loading("Download in progress…", 0);
    try {
      const urls = await Promise.all(
        allFiles.map(async (f) => ({
          url: await getS3DownloadUrl(f.bucket, f.key, msalInstance, selecteSpaceRegion, freshAgCreds),
          file: f,
        })),
      );

      const blobs = await Promise.all(
        urls.map(async ({ url, file }) => {
          if (!url) { message.error(`Failed link for "${file.name}"`); return null; }
          const r = await fetch(url);
          if (!r.ok) { message.error(`Failed fetch "${file.name}"`); return null; }
          return { file, blob: await r.blob() };
        }),
      );

      const fileBlobs = blobs.filter(Boolean) as { file: FileItem; blob: Blob }[];
      if (!fileBlobs.length) { message.error("Could not fetch any files."); return; }

      await fetchAndDownloadZip(fileBlobs, getCurrentFolderName() + ".zip");
      message.success("Download completed");
    } catch (e) {
      console.error(e);
      message.error("Download failed");
    } finally {
      end(); // always close the loading toast
    }
  };


  // simple concurrency limiter
  async function withLimit<T, R>(
    items: T[],
    limit: number,
    worker: (item: T, idx: number) => Promise<R>,
  ) {
    const out: R[] = new Array(items.length);
    let i = 0;
    const runners = new Array(Math.min(limit, items.length)).fill(0).map(async () => {
      while (i < items.length) {
        const idx = i++;
        out[idx] = await worker(items[idx], idx);
      }
    });
    await Promise.all(runners);
    return out;
  }

  // Download all files
  const downloadAll = async () => {

    // keep your original "all deleted" guard exactly as-is
    const availableItems = filteredData.filter((i) => !(i as any).__isDeleted);
    if (availableItems.length === 0) {
      return message.error("No downloadable files found. All objects are deleted.");
    }

    // Fetch fresh credentials before downloading
    const end = message.loading("Fetching credentials...", 0);
    let freshAgCreds: IGetAgCredsResponse | boolean;
    try {
      freshAgCreds = await getAgCreds(msalInstance, {
        bucket_name: spaceName,
        region: selecteSpaceRegion,
        data_account_id: DataAccountId,
        space_name: spaceDisplayName,
        access_level: "READ",
      });
      
      if (freshAgCreds === false) {
        end();
        message.error("Failed to get access credentials. Please try again later.");
        return;
      }
    } catch (err: any) {
      end();
      console.error("Error fetching credentials:", err);
      message.error("Failed to get access credentials. Please try again later.");
      return;
    }
    end();

    // NEW: always pull ALL files under the current folder from S3 (fully paginated)
    const currentPrefixStr = prefixTrail.join("");
    const allFilesRaw = await getAllFilesInFolder(
      currentPrefixStr,          // folderKey
      spaceName,                 // bucket
      msalInstance,
      selecteSpaceRegion,
      freshAgCreds,
    );

    // filter out deleted (if any marker is present)
    const allFiles = allFilesRaw.filter((f: any) => !(f).__isDeleted);

    // set relativePath so the zip preserves folder structure
    allFiles.forEach((f: any) => {
      let relPath = String(f.key).substring(currentPrefixStr.length);
      if (relPath.startsWith("/")) relPath = relPath.substring(1);
      f.relativePath = relPath || (f.name ?? f.key.split("/").pop());
    });

    if (!allFiles.length) {
      return message.error("No downloadable files found.");
    }

    const end2 = message.loading("Download in progress…", 0);
    try {
      const urls = await withLimit(allFiles, 10, async (f) => ({
        url: await getS3DownloadUrl(f.bucket, f.key, msalInstance, selecteSpaceRegion, freshAgCreds),
        file: f,
      }));

      const blobs = await withLimit(urls, 5, async ({ url, file }) => {
        if (!url) { message.error(`Failed link for "${file.name}"`); return null; }
        const r = await fetch(url);
        if (!r.ok) { message.error(`Failed fetch "${file.name}"`); return null; }
        return { file, blob: await r.blob() };
      });

      const fileBlobs = blobs.filter(Boolean) as any[];
      if (!fileBlobs.length) { message.error("Could not fetch any files."); return; }

      await fetchAndDownloadZip(fileBlobs, getCurrentFolderName() + ".zip");
      message.success("Download completed");
    } catch (e) {
      console.error(e);
      message.error("Download failed");
    } finally {
      end2(); // always close the loading toast
    }
  };

  // Helper function to check if any selected files are deleted
  const hasSelectedDeletedFiles = useMemo(() => {
    return selectedFiles.some(key => {
      const item = filteredData.find(f => f.key === key);
      return item && (item as any).__isDeleted;
    });
  }, [selectedFiles, filteredData]);


  // --- DELETE LOGIC ---
  const handleDeleteSelected = () => {
    if (!currentUserHasWriteAccess) {
      message.warning("You don't have write access to delete files.");
      return;
    }

    if (!selectedFiles.length) {
      message.warning("No files selected for deletion.");
      return;
    }

    if (hasSelectedDeletedFiles) {
      message.warning("You can't delete items that are already deleted. Deselect deleted items.");
      return;
    }
    setDeleteModalOpen(true);
  };

// Add this inside the FileGrid component (near other delete functions)
  const handleDeleteAll = async () => {
    if (!currentUserHasWriteAccess) {
      message.warning("You don't have write access to delete files.");
      return;
    }

    const currentPrefixStr = "";

    // For GXP spaces, show the delete modal with reason selection
    if (isGxpSpace) {
      try {
        setDeleteLoading(true);
        // Fetch ALL items in the bucket (include folder placeholders so empty folders are also deleted)
        const allFilesRaw = await getAllFilesInFolder(
          currentPrefixStr,
          spaceName,
          msalInstance,
          selecteSpaceRegion,
          agCredsResponseData,
          true,
        );

        // Filter out already deleted items
        const allFiles = (allFilesRaw || []).filter((f: any) => !(f).__isDeleted);

        if (!allFiles.length) {
          message.error("No deletable files found.");
          setDeleteLoading(false);
          return;
        }

        // Prepare keys and store them for the modal
        const keys = allFiles.map((f) => String(f.key));
        setEmptySpaceKeys(keys);
        setIsEmptySpaceMode(true);
        setDeleteLoading(false);
        setDeleteModalOpen(true);
      } catch (err) {
        console.error("Failed to fetch files for empty space:", err);
        message.error("Failed to fetch files.");
        setDeleteLoading(false);
      }
    } else {
      // For non-GXP spaces, use simple confirmation dialog
      Modal.confirm({
        title: "Empty Space",
        content: "Are you sure you want to delete ALL objects in this Space? This action cannot be undone.",
        okText: "Yes",
        okType: "danger",
        cancelText: "Cancel",
        onOk: async () => {
          setDeleteLoading(true);
          try {
            // 1) Fetch ALL items in the bucket (include folder placeholders so empty folders are also deleted)
            const allFilesRaw = await getAllFilesInFolder(
              currentPrefixStr,          // folderKey
              spaceName,                 // bucket
              msalInstance,
              selecteSpaceRegion,
              agCredsResponseData,
              true,
            );

          // 2) Filter out already deleted items (if your objects include __isDeleted flag)
          const allFiles = (allFilesRaw || []).filter((f: any) => !(f).__isDeleted);

          if (!allFiles.length) {
            message.error("No deletable files found.");
            return;
          }

          // 3) Prepare keys to send to delete API
          const keys = allFiles.map((f) => String(f.key));

          // Optional: ask for a short reason (here we supply a default)
          const reason = `Bulk delete from UI by ${getLoggedInUser() || "unknown user"}`;

          // 4) Call your server-side delete API (same API used by handleDeleteModalOk)
          await deleteFilesAPI({
            region: region || "us-east-1",
            bucket: spaceName,
            keys,
            reason,
            permanentDelete: false, // change if you want permanent deletion by default
            domainName: domainName,
            spaceName: spaceDisplayName,
            userEmail: getLoggedInUser(),
            msalInstance,
            spaceType: gxpFlag,
          });

          message.success("Delete request queued. You will be notified when complete.");

          // 5) Refresh UI & clear selections (same pattern as after deleteSelected)
          setRefreshKey((k) => k + 1);
          onFolderClick?.(
            spaceDisplayName,
            selecteSpaceRegion,
            "", // root prefix
            selectedBucket,
            spaceDisplayName,
          );
          setSelectedFiles([]);
        } catch (err) {
          console.error("Failed to delete all files:", err);
          message.error("Failed to delete all files.");
        } finally {
          setDeleteLoading(false);
        }
      },
    });
    }
  };


  const handleDeleteModalOk = async (reason: string, permanent: boolean) => {
    setDeleteLoading(true);
    try {
      const userEmail = getLoggedInUser();

      // Determine which keys to delete: empty space mode or selected files
      const keysToDelete = isEmptySpaceMode ? emptySpaceKeys : selectedFiles;

      await deleteFilesAPI({
        region: region || "us-east-1",
        bucket,
        keys: keysToDelete,
        reason,
        permanentDelete: permanent,
        domainName: domainName,
        spaceName: spaceDisplayName,
        userEmail,
        msalInstance,
        spaceType: gxpFlag, // Pass the space type to the API
      });
      message.success("Your request for deleting the selected files have been queued and you will be notified accordingly when the process is completed.");
      setDeleteModalOpen(false);
      setIsEmptySpaceMode(false);
      setEmptySpaceKeys([]);

      // Refresh and clear
      setRefreshKey((k) => k + 1);

      // If empty space mode, go to root; otherwise stay in current folder
      const targetPrefix = isEmptySpaceMode ? "" : prefixTrail.join("");
      onFolderClick?.(spaceDisplayName, selecteSpaceRegion, targetPrefix, selectedBucket, spaceDisplayName);

      setSelectedFiles([]);

      // Reset empty space mode
      if (isEmptySpaceMode) {
        setIsEmptySpaceMode(false);
        setEmptySpaceKeys([]);
      }
    } catch (err) {
      message.error("Failed to delete files");
    } finally {
      setDeleteLoading(false);
    }
  };

  // Add folder logic
  const handleAddFolder = async () => {
    if (!newFolderName.trim()) {
      setAddFolderError("Folder name cannot be empty.");
      return;
    }
    const existing = filteredData
      .filter((i) => i.isFolder)
      .map((i) => i.name.toLowerCase().replace(/\/$/, ""));
    if (existing.includes(newFolderName.trim().toLowerCase())) {
      setAddFolderError("A folder with this name already exists.");
      return;
    }
    setAddFolderError("");
    setIsAddFolderModalOpen(false);

    // Fetch fresh credentials before creating folder
    let freshAgCreds: IGetAgCredsResponse | boolean;
    try {
      freshAgCreds = await getAgCreds(msalInstance, {
        bucket_name: spaceName,
        region: selecteSpaceRegion,
        data_account_id: DataAccountId,
        space_name: spaceDisplayName,
        access_level: "READWRITE",
      });
      
      if (freshAgCreds === false) {
        message.error("Failed to get access credentials. Please try again later.");
        return;
      }
    } catch (err: any) {
      console.error("Error fetching credentials:", err);
      message.error("Failed to get access credentials. Please try again later.");
      return;
    }

    try {
      await createS3Folder(
        spaceName,
        selecteSpaceRegion,
        prefixTrail.join("") + newFolderName.trim() + "/",
        msalInstance,
        freshAgCreds,
      );
      message.success("Folder created successfully!");
      setRefreshKey((k) => k + 1);
      onFolderClick?.(spaceDisplayName, selecteSpaceRegion, prefixTrail.join(""), selectedBucket, spaceDisplayName);
      setNewFolderName("");
    } catch {
      message.error("Failed to create folder.");
    }
  };

const hasDownloadableFiles = useMemo(() => {

    if (showVersions) {
      const activeItems = filteredData.filter((i) => {
        const isDeleted =
          !!(i as any).__isDeleted ||
          i.class === "DELETED" ||
          (typeof i.name === "string" && i.name.endsWith(" (deleted)"));

        return !isDeleted;
      });
      return activeItems.length > 0;
    } else {
      const downloadableItems = filteredData.filter((i) => !(i as any).__isDeleted);
      return downloadableItems.length > 0;
    }
  }, [filteredData, showVersions]);

  const hasDeletableFiles = useMemo(() => {
    if (showVersions) {
      const activeItems = filteredData.filter((i) => {
        const isDeleted =
          !!(i as any).__isDeleted ||
          i.class === "DELETED" ||
          (typeof i.name === "string" && i.name.endsWith(" (deleted)"));

        return !isDeleted;
      });
      return activeItems.length > 0;
    } else {
    // In normal mode, also check for items that don't have delete markers
    const deletableItems = filteredData.filter((i) => {
      const isDeleted =
        !!(i as any).__isDeleted ||
        i.class === "DELETED" ||
        (typeof i.name === "string" && i.name.endsWith(" (deleted)"));

      return !isDeleted;
      });
      return deletableItems.length > 0;
    }
  }, [filteredData, showVersions]);

// Register asset in Datazone
  const handleRegisterAsset = async (
    bucket: string,
    key: string,
    rec: FileItem,
  ) => {
    message.loading({ content: `Registering ${rec.name}…`, key: `reg-${key}` });
    const res = await registerAssetInDatazone(bucket, key, msalInstance, selecteSpaceRegion);
    if (res.success) {
      setRegisteredKeys((p) => [...p, key]);
      message.success({ content: res.message, key: `reg-${key}` });
    } else {
      message.error({ content: res.message, key: `reg-${key}` });
    }
  };

  // Add this function to handle preview
  const handlePreviewFile = async (rec: FileItem) => {
    try {
      const url = await getS3DownloadUrl(rec.bucket, rec.key, msalInstance, selecteSpaceRegion, agCredsResponseData);

      setPreviewFileUrl(url);
      setPreviewFileName(rec.name);
      setPreviewModalOpen(true);
    } catch {
      message.error("Failed to load file preview");
    }
  };

  // Helper function to check if all selected files are deleted
  const allSelectedFilesAreDeleted = useMemo(() => {
    if (!selectedFiles.length) return false;
    return selectedFiles.every(key => {
      const item = filteredData.find(f => f.key === key);
      // Only consider an item "deleted" if it's actually marked as deleted, not if it's a folder
      return item && (item as any).__isDeleted;
    });
  }, [selectedFiles, filteredData]);

  // Determine if delete option should be enabled for FSx based on file system type
  const enableDeleteFsxOption = fileSystemType === "fsx";
  const isOwner = currentUserRole === "owner";

  const handleDeleteFsx = () => {
    Modal.confirm({
      title: "Delete FSx File System",
      content: isOwner
        ? `Are you sure you want to delete the FSx file system for "${spaceDisplayName}"? This action cannot be undone.`
        : "You must be an owner of this space to delete the FSx file system.",
      okText: "Confirm",
      okType: "danger",
      okButtonProps: { disabled: !isOwner },
      cancelText: "Cancel",
      onOk: async () => {
        const hide = message.loading("Deleting FSx file system…", 0);
        try {
          const response = await deleteCustomAttachedFilesystem(msalInstance, { space_name: spaceDisplayName });          
          message.success(response?.message || "FSx file system deletion initiated successfully.");
        } catch (err: any) {
          console.error("Failed to delete FSx:", err);
          message.error(err?.response?.data?.context?.message || "Failed to delete FSx file system.");
        } finally {
          hide();
        }
      },
    });
  };

  // Actions menu for settings
  const actionsMenu = (
    <Menu className="settingsDropdownMenu" style={{ width: 190 }}>
      <Menu.Item
        key="editSpace"
        icon={<EditOutlined style={{ fontSize: 16 }} />}
        style={{ color: "#1677ff", fontWeight: 500, fontSize: 16, paddingLeft: 24 }}
      >
        <AddOrEditSpace
          mode="edit"
          editPayload={{
            bucketName: spaceDisplayName,
            spaceName: prefixTrail.length
              ? prefixTrail[0].replace(/\/$/, "")
              : spaceName,
            region: region || "us-east-1",
            domainName: domainName,
            actualBucketName: spaceName, // actually bucketname
          }}
          onSuccess={onRefreshSpaces}
          buttonText="Edit Space"
          buttonProps={{
            type: "link",
            style: {
              padding: 0,
              color: "#1677ff",
              fontWeight: 500,
              background: "none",
              border: "none",
              boxShadow: "none",
              height: "auto",
              textAlign: "left",
            },
            icon: null,
          }}
        />
      </Menu.Item>

      <Menu.Item
        key="downloadSelected"
        icon={<DownloadOutlined style={{ fontSize: 16 }} />}
        disabled={!selectedFiles.length || allSelectedFilesAreDeleted}
        onClick={downloadSelected}
      >
        Download Selected
      </Menu.Item>
      <Menu.Item
        key="downloadAll"
        icon={<DownloadOutlined style={{ fontSize: 16 }} />}
        disabled={!hasDownloadableFiles} // Add this line to disable when no downloadable files
        onClick={downloadAll}
        style={{
          color: !hasDownloadableFiles ? "#d9d9d9" : "#000", // Gray out when disabled
        }}
      >
        Download All
      </Menu.Item>

      {(!currentUserHasWriteAccess) ? (
        <Tooltip
          title={"You don't have write access to this space"}
          placement="right"
        >
          <div style={{ cursor: "not-allowed" }}>
            <Menu.Item
              key="deleteSelected"
              icon={<DeleteOutlined style={{ fontSize: 16 }} />}
              disabled
              style={{
                color: "#d9d9d9",
                cursor: "not-allowed",
                pointerEvents: "none",
              }}
            >
              Delete Selected
            </Menu.Item>
          </div>
        </Tooltip>
      ) : (
        <Menu.Item
          key="deleteSelected"
          icon={<DeleteOutlined style={{ fontSize: 16 }} />}
          disabled={!selectedFiles.length || hasSelectedDeletedFiles}
          onClick={handleDeleteSelected}
        >
          Delete Selected
        </Menu.Item>
      )}
      {(!currentUserHasWriteAccess || !filteredData.length || !hasDeletableFiles) ? (
        <Tooltip
          title={!currentUserHasWriteAccess ? "You don't have write access to this space" : !filteredData.length ? "No items available to delete" : !hasDeletableFiles ? "No deletable files available" : "Clear space" }
          placement="right"
        >
          <div style={{ cursor: "not-allowed" }}>
            <Menu.Item
              key="emptySpace"
              icon={<DeleteOutlined style={{ fontSize: 16 }} />}
              disabled
              style={{
                color: "#d9d9d9",
                cursor: "not-allowed",
                pointerEvents: "none",
              }}
            >
              Empty Space
            </Menu.Item>
          </div>
        </Tooltip>
      ) : (
        <Menu.Item
          key="deleteAll"
          icon={<DeleteOutlined style={{ fontSize: 16 }} />}
          onClick={handleDeleteAll}
        >
          Empty Space
        </Menu.Item>
      )
      }
    

      {isUploadDisabled || !currentUserHasWriteAccess ? (
        <Tooltip title={isUploadDisabled ? "Disabled for GxP spaces" : "You don't have write access to this space"} placement="right">
          <div style={{ cursor: "not-allowed" }}>
            <Menu.Item
              key="addFolder"
              icon={<FolderAddOutlined style={{ fontSize: 16 }} />}
              disabled={true}
              style={{
                color: "#d9d9d9",
                cursor: "not-allowed",
                pointerEvents: "none",
              }}
            >
              Add New Folder
            </Menu.Item>
          </div>
        </Tooltip>
      ) : (
        <Menu.Item
          key="addFolder"
          icon={<FolderAddOutlined style={{ fontSize: 16 }} />}
          onClick={() => setIsAddFolderModalOpen(true)}
        >
          Add New Folder
        </Menu.Item>)}
      {isUploadDisabled || !currentUserHasWriteAccess ? (
        <Tooltip title={isUploadDisabled ? "Disabled for GxP spaces" : "You don't have write access to this space"} placement="right">
          <div style={{ cursor: "not-allowed" }}>
            <Menu.Item
              key="upload"
              icon={<UploadOutlined style={{ fontSize: 16 }} />}
              disabled={true}
              style={{
                color: "#d9d9d9",
                cursor: "not-allowed",
                pointerEvents: "none",
              }}
            >
              Upload Files
            </Menu.Item>
          </div>
        </Tooltip>
      ) : (
        <Menu.Item
          key="upload"
          icon={<UploadOutlined style={{ fontSize: 16 }} />}
          onClick={() => uploadRef.current?.open()}
        >
          Upload Files
        </Menu.Item>
      )}
      {
        enableDeleteFsxOption && (
          <Menu.Item
            key="deleteFsx"
            icon={<DeleteOutlined style={{ fontSize: 16 }} />}
            onClick={handleDeleteFsx}
          >
            Delete FSx
          </Menu.Item>
        ) 
      }
    </Menu>
  );

  const handleRefresh = async () => {
    setLoading(true);
    setApiError(null);
    try {
      const tokenForThisPage = pageTokens[currentPage - 1];
      // Use the same logic as the main useEffect - check showVersions state
      const objectListResp = showVersions
        ? await (await import("./objectListApi")).getObjectsWithVersions(
          msalInstance,
          spaceName,
          selecteSpaceRegion,
          prefixTrail.join("") || "",
          agCredsResponseData,
          {
            maxKeys: pageSize,
            continuationToken: tokenForThisPage,
          },
        )
        : await (await import("./objectListApi")).getObjectsByDomainName(
          msalInstance,
          [spaceName],
          selecteSpaceRegion,
          prefixTrail.join("") || "",
          agCredsResponseData,
          {
            maxKeys: pageSize,
            continuationToken: tokenForThisPage,
          },
        );

      setObjectList(objectListResp ?? { data: [] });

      // Also refresh version summary
      const versionSummaryResp = await getVersionsSummaryForPrefix(
        msalInstance,
        spaceName,
        selecteSpaceRegion,
        prefixTrail.join("") || "",
        agCredsResponseData,
      );
      setVersionSummary(versionSummaryResp);

      setSelectedFiles([]);
      message.success("Folder refreshed");
    } catch (err) {
      setApiError("Failed to load objects. Please try again.");
      setObjectList({ data: [] });
      message.error("Refresh failed");
    } finally {
      setLoading(false);
    }
  };

  const ChevronButton: React.FC<{ expanded: boolean; onClick: () => void; }> = ({ expanded, onClick }) => (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className="chevron-button"
      aria-label={expanded ? "Collapse versions" : "Expand versions"}
      title={expanded ? "Collapse versions" : "Expand versions"}
    >
      {expanded ? <DownOutlined /> : <RightOutlined />}
    </button>
  );

  const expandCol = {
    title: "",
    dataIndex: "__expander",
    key: "__expander",
    width: 48,
    render: (_: any, rec: FileItem) => {
      if (!showVersions || rec.isFolder) return null;
      const isExpanded = expandedRowKeys.includes(rec.key);
      return (
        <ChevronButton
          expanded={isExpanded}
          onClick={() => {
            setExpandedRowKeys((prev) =>
              prev.includes(rec.key) ? prev.filter(k => k !== rec.key) : [...prev, rec.key],
            );
          }}
        />
      );
    },
  };

  // Table columns (with sorting, "time ago", etc)
  const columns = [
    {
      title: "Object",
      dataIndex: "name",
      key: "name",
      width: 300,
      sorter: (a: FileItem, b: FileItem) => a.name.localeCompare(b.name),

      render: (_: any, rec: FileItem & { __isDeleted?: boolean }) => {
        const isFolder = rec.isFolder;

        // 👇 consider multiple signals for "deleted"
        const isDeleted =
          !!rec.__isDeleted ||
          rec.class === "DELETED" ||
          (typeof rec.name === "string" && rec.name.endsWith(" (deleted)"));

        const handleClick = () => {
          if (!isFolder) return;
          // allow navigation even for deleted folder
          handleFolderClick(rec.name.replace(" (deleted)", "") + "/");
        };

        const nameNode = (
          <span
            className={`object-name ${isFolder ? "folder" : "file"} ${isDeleted ? "deleted" : "normal"
              }`}
            style={{
              cursor: isFolder ? "pointer" : isDeleted ? "not-allowed" : "default",
            }}
            onClick={isFolder ? handleClick : undefined}
            title={rec.name}
          >
            {rec.name}
          </span>
        );

        const tooltipTitle =
          isDeleted && isFolder
            ? "This folder has been deleted"
            : isDeleted && !isFolder
              ? "This file has been deleted"
              : "";

        const content = tooltipTitle ? (
          <Tooltip title={tooltipTitle} overlayClassName="deleted-object-tooltip">
            {nameNode}
          </Tooltip>
        ) : (
          nameNode
        );

        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {content}

            {/* Version badge for files with previous versions */}
            {!isFolder &&
              rec.previousVersionCount !== undefined &&
              rec.previousVersionCount > 0 && (
                <Tooltip
                  title={
                    rec.previousVersionCount === 1
                      ? "1 previous version available"
                      : `${rec.previousVersionCount} previous versions available`
                  }
                >
                  <span className="version-badge">
                    {rec.previousVersionCount > 99 ? "99+" : rec.previousVersionCount}
                  </span>
                </Tooltip>
              )}
          </div>
        );
      },
    },
    {
      title: "Last Modified",
      dataIndex: "lastModified",
      key: "lastModified",
      sorter: (a: FileItem, b: FileItem) =>
        new Date(a.lastModified ?? 0).getTime() -
        new Date(b.lastModified ?? 0).getTime(),
      render: (v: string | null) => timeAgo(v),
    },
    {
      title: "Region",
      dataIndex: "region",
      key: "region",
      sorter: (a: FileItem, b: FileItem) => (a.region || "").localeCompare(b.region || ""),
      render: (v: string) => v || "-",
    },
    {
      title: "Timestamp",
      dataIndex: "timestamp",
      key: "timestamp",
      sorter: (a: FileItem, b: FileItem) =>
        new Date(a.timestamp ?? 0).getTime() -
        new Date(b.timestamp ?? 0).getTime(),
      render: (v: string) => (v ? new Date(v).toLocaleString() : "-"),
    },
    {
      title: "Class",
      dataIndex: "class",
      key: "class",
      sorter: (a: FileItem, b: FileItem) => (a.class || "").localeCompare(b.class || ""),
      render: (v: string) => v || "-",
    },
    {
      title: "Size",
      dataIndex: "size",
      key: "size",
      sorter: (a: FileItem, b: FileItem) => (a.size || 0) - (b.size || 0),
      render: (v: { toString: () => string } | null) =>
        v != null ? formatBytes(v.toString()) : "",
    },
    {
      title: "Action",
      key: "action",
      render: (_: any, rec: FileItem & { __isDeleted?: boolean }) => (
        <Dropdown
          overlay={
            <Menu className="action-menu-custom">
              <Menu.Item
                key="copyUri"
                style={{
                  color: rec.__isDeleted ? "#d9d9d9" : "#1677ff",
                  fontWeight: 500,
                  fontSize: 15,
                  display: "flex",
                  alignItems: "center",
                  cursor: rec.__isDeleted ? "not-allowed" : "pointer",
                }}
                disabled={rec.__isDeleted}
                onClick={async () => {
                  if (rec.__isDeleted) return;
                  try {
                    await navigator.clipboard.writeText(
                      `s3://${rec.bucket}/${rec.key}`,
                    );
                    message.success("S3 URI Copied to Clipboard");
                  } catch {
                    message.error("Failed to copy S3 URI");
                  }
                }}
              >
                <CopyOutlined style={{ marginRight: 10, fontSize: 16 }} />
                Copy URI
              </Menu.Item>

              {/* Eye icon for file preview - disabled for folders and deleted objects */}
              {!rec.isFolder && ENABLE_FILE_PREVIEW && (
                <Menu.Item
                  key="preview"
                  style={{
                    color: rec.__isDeleted ? "#d9d9d9" : "#1677ff",
                    fontWeight: 500,
                    fontSize: 15,
                    display: "flex",
                    alignItems: "center",
                    cursor: rec.__isDeleted ? "not-allowed" : "pointer",
                  }}
                  disabled={rec.__isDeleted}
                  onClick={() => {
                    if (rec.__isDeleted) return;
                    handlePreviewFile(rec);
                  }}
                >
                  <EyeOutlined style={{ marginRight: 10, fontSize: 16 }} />
                  Preview
                </Menu.Item>
              )}
            </Menu>
          }
          trigger={["hover"]}
          placement="bottomLeft"
          overlayClassName="action-menu-dropdown"
        >
          <span className="more-actions-button">
            <MoreOutlined />
          </span>
        </Dropdown>
      ),
    },
  ];

  const hasSelectedFiles = useMemo(
    () => filteredData.some(f => selectedFiles.includes(f.key) && !f.isFolder),
    [filteredData, selectedFiles],
  );

  // === Download "versions record" (CSV per file; ZIP when multiple) ===
  function toCsv(rows: Array<Record<string, any>>): string {
    const esc = (v: unknown) => {
      if (v === null || v === undefined) return "";
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    if (!rows.length) return "";
    const headers = Object.keys(rows[0]);
    const head = headers.map(esc).join(",");
    const body = rows.map(r => headers.map(h => esc(r[h])).join(",")).join("\n");
    return `${head}\n${body}`;
  }

  function baseNameFromKeyOrName(key: string, name: string) {
    const raw = name || key.split("/").pop() || "file";
    return raw.replace(/[^a-zA-Z0-9._-]/g, "_");
  }

  async function fetchAllVersionsForFile(file: FileItem) {
    let keyMarker: string | undefined = undefined;
    let versionIdMarker: string | undefined = undefined;
    const all: CombinedVersion[] = [];
    do {
      const page: any = await getObjectVersionsPage(
        file.bucket,
        file.key,
        msalInstance,
        selecteSpaceRegion,
        agCredsResponseData,
        { keyMarker, versionIdMarker, maxKeys: 1000 },
      );
      all.push(...page.versions);
      keyMarker = page.nextKeyMarker;
      versionIdMarker = page.nextVersionIdMarker;
      if (!page.isTruncated) break;
    } while (true);
    return all;
  }

  async function handleDownloadSelectedVersionsRecords() {
    const selectedFileRows = filteredData.filter(
      (i) => selectedFiles.includes(i.key) && !i.isFolder,
    );
    if (!selectedFileRows.length) {
      message.warning("Select one or more files (not folders).");
      return;
    }

    const loadingKey = "dl-versions-records";
    message.loading({ content: "Preparing versions…", key: loadingKey, duration: 0 });

    try {
      if (selectedFileRows.length === 1) {
        // Single CSV
        const file = selectedFileRows[0];
        const versions = await fetchAllVersionsForFile(file);

        const rows = versions.map((v) => ({
          Type: v.__type === "DeleteMarker" ? "Delete marker" : "Version",
          VersionId: "VersionId" in v ? (v.VersionId ?? "") : "",
          LastModified: v.LastModified ? timeAgo(new Date(v.LastModified).toISOString()) : "-",
          Region: region || "",
          TimeStamp: v.LastModified ? new Date(v.LastModified as any).toLocaleString() : "",
          StorageClass: v.__type === "DeleteMarker" ? "-" : ((v as any).StorageClass ?? "-"),
          Size:
            v.__type === "DeleteMarker"
              ? "-"
              : typeof (v as any).Size === "number"
                ? formatBytes(String((v as any).Size))
                : "-",
          IsLatest: v.IsLatest ? "Yes" : "No",
        }));

        const csv = toCsv(rows);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${baseNameFromKeyOrName(file.key, file.name)}_versions.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setSelectedFiles([]);
      } else {
        // ZIP multiple CSVs
        const zip = new JSZip();

        for (const file of selectedFileRows) {
          const versions = await fetchAllVersionsForFile(file);

          const rows = versions.map((v) => ({
            Type: v.__type === "DeleteMarker" ? "Delete marker" : "Version",
            VersionId: "VersionId" in v ? (v.VersionId ?? "") : "",
            LastModified: v.LastModified ? timeAgo(new Date(v.LastModified).toISOString()) : "-",
            Region: region || "",
            TimeStamp: v.LastModified ? new Date(v.LastModified as any).toLocaleString() : "",
            StorageClass: v.__type === "DeleteMarker" ? "-" : ((v as any).StorageClass ?? "-"),
            Size:
              v.__type === "DeleteMarker"
                ? "-"
                : typeof (v as any).Size === "number"
                  ? formatBytes(String((v as any).Size))
                  : "-",
            IsLatest: v.IsLatest ? "Yes" : "No",
          }));

          const csv = toCsv(rows);
          const fname = `${baseNameFromKeyOrName(file.key, file.name)}_versions.csv`;
          zip.file(fname, csv);
        }

        const blob = await zip.generateAsync({ type: "blob" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `versions_records_${getCurrentFolderName()}.zip`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }

      message.success({ content: "Download Completed", key: loadingKey });
    } catch (err) {
      console.error(err);
      message.error({ content: "Failed to download versions", key: loadingKey });
    }
  }

  // Decide if there is another page after the current one
  const hasMorePages =
    showVersions
      ? filteredData.length > currentPage * pageSize // client-side "has more"
      : !!lastS3NextToken;                           // S3 token-based "has more"

  // Total for AntD pagination: current pages + at most 1 extra page
  const totalForPagination =
    currentPage * pageSize + (hasMorePages ? 1 : 0);


  return (
    <div className="fileGridContainer" ref={objectListRef}>
      <div className="filegrid-toolbar" style={{ display: "flex", alignItems: "center", gap: 16 }}>
        {/* --- Back Button (only shows if not at root) --- */}
        {prefixTrail.length > 0 && (
          <Tooltip title="Back">
            <button
              className="back-button"
              onClick={() => {
                if (prefixTrail.length === 0) return;

                const n = prefixTrail.length;
                // If we are currently inside "--landing/", then pop off both
                // "--landing/" and its parent folder in one go:
                const isInLanding = prefixTrail[n - 1] === "--landing/";
                const newTrail = isInLanding
                  ? prefixTrail.slice(0, n - 2)
                  : prefixTrail.slice(0, n - 1);

                setPrefixTrail(newTrail);
                onFolderClick?.(spaceDisplayName, selecteSpaceRegion, newTrail.join(""), selectedBucket, spaceDisplayName);
                setSearchTerm("");

                setCurrentPage(1);
                setPageTokens([undefined]);
                setLastS3NextToken(undefined);
              }}
            >
              ← Back
            </button>
          </Tooltip>
        )}

        <div className="breadcrumbAndSettings" style={{ flex: 1 }}>
          <Breadcrumb style={{ margin: "0" }}>
            <Breadcrumb.Item>{domainName.toUpperCase()}</Breadcrumb.Item>
            {spaceName && (
              <Breadcrumb.Item onClick={() => handleBreadcrumbClick(-1)}>
                {spaceDisplayName && spaceDisplayName.toUpperCase()}
              </Breadcrumb.Item>
            )}
            {prefixTrail.map((p, i) => (
              <Breadcrumb.Item key={i} onClick={() => handleBreadcrumbClick(i)}>
                {p.replace(/\/$/, "")}
              </Breadcrumb.Item>
            ))}
          </Breadcrumb>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Tooltip title={
            isUploadDisabled
              ? "Upload is disabled for GxP spaces"
              : !currentUserHasWriteAccess
                ? "You don't have write access to this space"
                : "Upload Files"
          }>
            <button
              className={`uploadIconButton ${(isUploadDisabled || !currentUserHasWriteAccess)
                ? "upload-button-disabled"
                : "upload-button-enabled"
                }`}
              type="button"
              style={{
                border: "none",
                background: "none",
                fontSize: "20px",
                borderRadius: "96px",
                transition: "background 0.2s",
              }}
              onClick={() => !isUploadDisabled && currentUserHasWriteAccess && uploadRef.current?.open()}
              disabled={isUploadDisabled || !currentUserHasWriteAccess}
            >
              <UploadOutlined />
            </button>
          </Tooltip>
        </div>
        <Dropdown
          overlay={actionsMenu}
          trigger={["click"]}
          placement="bottomRight"
          overlayClassName="settings-dropdown-custom"
          arrow
          getPopupContainer={() => document.body}
        >
          <Tooltip title="Settings">
            <button className="settingsGearButton" type="button">
              <SettingOutlined />
            </button>
          </Tooltip>
        </Dropdown>
      </div>
      <Collapse defaultActiveKey={["1"]} style={{ backgroundColor: "#f5f5f6" }}>

        <Collapse.Panel
          key="1"
          collapsible="icon"
          header={
            <div
              className="panel-header-row"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >

              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span>
                  Files inside <strong>{spaceDisplayName}</strong>
                </span>


                <div
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <Tooltip title="versions view for all files">
                    <Switch
                      checked={showVersions}
                      onChange={(checked) => {
                        setShowVersions(checked);
                        setExpandedRowKeys([]);
                        setSelectedFiles([]);

                        setCurrentPage(1);
                        setPageTokens([undefined]);
                        setLastS3NextToken(undefined);
                      }}
                      checkedChildren="Versions"
                      unCheckedChildren="Versions"
                    />
                  </Tooltip>
                </div>
              </div>

              <div
                style={{ display: "flex", alignItems: "center", gap: 8 }}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
              >
                
                <Input.Search
                  placeholder="Search files"
                  allowClear
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                  }}
                  style={{ width: 220 }}
                />
                <Tooltip title="Refresh">
                  <Button
                    type="text"
                    icon={<SyncOutlined spin={loading} />}
                    onClick={handleRefresh}
                  />
                </Tooltip>
              </div>
            </div>
          }

        >
          <Box sx={{ width: "100%" }}>
            {loading ? (
              <FormSkeleton layout={formSkeleton} />
            ) : apiError ? (
              <Empty description={apiError} />
            ) : filteredData.length === 0 ? (
              <Empty description="No files found" />
            ) : (
              <>
                <Table
                  dataSource={filteredData}
                  columns={[expandCol, ...columns]}
                  pagination={{
                    current: currentPage,
                    pageSize,
                    total: totalForPagination,
                    showSizeChanger: true,
                    pageSizeOptions: ["5", "10", "20", "50", "100"],
                    onChange: (nextPage, nextPageSize) => {
                      if (nextPageSize !== pageSize) {
                        setPageSize(nextPageSize);
                        setCurrentPage(1);
                        setPageTokens([undefined]);
                        setLastS3NextToken(undefined);
                        return;
                      }

                      setCurrentPage(nextPage);

                      // 👇 IMPORTANT: allow S3 pagination even in versions mode
                      if (nextPage > pageTokens.length && lastS3NextToken) {
                        setPageTokens(prev => {
                          const copy = [...prev];
                          copy[nextPage - 1] = lastS3NextToken;
                          return copy;
                        });
                      }
                    },
                  }}



                  rowKey="key"
                  rowSelection={{
                    selectedRowKeys: selectedFiles,
                    onChange: (keys) => setSelectedFiles(keys as string[]),
                    // selections: [Table.SELECTION_ALL, Table.SELECTION_NONE],
                    getCheckboxProps: (record: FileItem & { __isDeleted?: boolean }) => ({
                      // Add visual indicator for deleted items
                      style: record.__isDeleted ? { opacity: 0.5 } : undefined,
                    }),
                  }}
                  scroll={{ x: true }}
                  rowClassName={(record: FileItem & { __isDeleted?: boolean }) => {
                    const isDeleted =
                      !!record.__isDeleted ||
                      record.class === "DELETED" ||
                      (record?.name && record.name.endsWith(" (deleted)"));

                    return isDeleted ? "row-deleted-object" : "";
                  }}

                  components={{
                    body: {
                      row: (props: any) => {
                        const { children, className, ...restProps } = props;
                        const rowKey = restProps["data-row-key"];
                        const record = filteredData.find(
                          item => item.key === rowKey,
                        ) as (FileItem & { __isDeleted?: boolean }) | undefined;

                        const isDeleted =
                          !!record?.__isDeleted ||
                          record?.class === "DELETED" ||
                          (record?.name && record.name.endsWith(" (deleted)"));

                        if (isDeleted && showVersions && record) {
                          return (
                            <Tooltip
                              title={
                                <div>
                                  {record.isFolder
                                    ? "This folder has been deleted"
                                    : "This file has been deleted"}
                                </div>
                              }
                              placement="top"
                              overlayClassName="deleted-object-tooltip"
                              align={{ offset: [0, -8] }}
                              arrowPointAtCenter={true}
                            >
                              <tr {...restProps} className={className}>
                                {children}
                              </tr>
                            </Tooltip>
                          );
                        }

                        return (
                          <tr {...restProps} className={className}>
                            {children}
                          </tr>
                        );
                      },
                    },
                  }}

                  expandable={{
                    expandIconColumnIndex: -1,
                    expandedRowKeys,
                    onExpand: (expanded, rec) => {
                      if (rec.isFolder || !showVersions) return;

                      setExpandedRowKeys((prev) =>
                        expanded ? [...prev, rec.key] : prev.filter((k) => k !== rec.key),
                      );

                      if (expanded) {
                        // Force a fresh mount so InlineVersionsTable refetches & shows spinner
                        setRowVersionMountKeyByKey((prev) => ({
                          ...prev,
                          [rec.key]: Date.now(),
                        }));
                      }
                    },
                    expandedRowRender: (rec) =>
                      showVersions && !rec.isFolder ? (
                        <InlineVersionsTable
                          key={`versions-${rec.key}-${rowVersionMountKeyByKey[rec.key] || 0}`}
                          bucket={rec.bucket}
                          objectKey={rec.key}
                          name={rec.name}
                          msalInstance={msalInstance}
                          region={selecteSpaceRegion}
                          agCredsResponseData={agCredsResponseData}
                          pageSize={10}
                          active={expandedRowKeys.includes(rec.key)}
                        />
                      ) : null,

                  }}
                />


                <div style={{ textAlign: "right", marginTop: 8 }}>
                  Showing{" "}
                  {Math.min((currentPage - 1) * pageSize + 1, filteredData.length)}{" "}
                  to{" "}
                  {Math.min(currentPage * pageSize, filteredData.length)} of{" "}
                  {filteredData.length}
                </div>
              </>
            )}
          </Box>
        </Collapse.Panel>

      </Collapse>

      {/* Delete Modal */}
      <DeleteFileModal
        visible={deleteModalOpen}
        onOk={handleDeleteModalOk}
        onCancel={() => {
          setDeleteModalOpen(false);
          setIsEmptySpaceMode(false);
          setEmptySpaceKeys([]);
        }}
        loading={deleteLoading}
        fileCount={isEmptySpaceMode ? emptySpaceKeys.length : selectedFiles.length}
        spaceType={gxpFlag} // Pass the space type to the modal
      />

      {/* Add New Folder Modal */}
      <Modal
        title="Add New Folder"
        visible={isAddFolderModalOpen}
        onOk={handleAddFolder}
        onCancel={() => {
          setIsAddFolderModalOpen(false);
          setAddFolderError("");
          setNewFolderName("");
        }}
        okText="Create"
      >
        <div>
          <div style={{ marginBottom: 8 }}>
            <b>Current Path:</b> {prefixTrail.join("")}
          </div>
          <Input
            autoFocus
            placeholder="Enter folder name"
            value={newFolderName}
            onChange={(e) => {
              setNewFolderName(e.target.value);
              setAddFolderError("");
            }}
          />
          {addFolderError && (
            <div style={{ color: "red", marginTop: 8 }}>{addFolderError}</div>
          )}
        </div>
      </Modal>

      {/* Upload Modal */}
      <UploadComponent
        agCredsResponseData={agCredsResponseData}
        ref={uploadRef}
        spaceName={spaceName}
        spaceDisplayName={spaceDisplayName}
        selectedSpaceRegion={selecteSpaceRegion}
        path={prefixTrail.join("")}
        dataAccountId={DataAccountId}
        domainName={domainName}
        uploaderEmail={getLoggedInUser()}
        onUploadComplete={() => {
          setRefreshKey((k) => k + 1);
          onFolderClick?.(spaceDisplayName, selecteSpaceRegion, prefixTrail.join(""), selectedBucket, spaceDisplayName);
          message.success("Upload complete. Refreshing view...");
        }}
      />
      <FilePreviewModal
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        fileUrl={previewFileUrl}
        fileName={previewFileName}
      />
    </div>
  );

};

export default FileGrid;